import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Building2, X, Loader2 } from 'lucide-react'
import api from '@/services/api'
import { cn } from '@/utils/cn'
import type { EmpresaDetailed } from '@/types'

interface CompanySearchSelectProps {
  onSelect: (company: EmpresaDetailed) => void
  onClear: () => void
  selectedCompany?: EmpresaDetailed | null
  error?: string
}

export function CompanySearchSelect({
  onSelect,
  onClear,
  selectedCompany,
  error,
}: CompanySearchSelectProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<EmpresaDetailed[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([])
      return
    }
    setLoading(true)
    try {
      const { data } = await api.get('/empresas', {
        params: { search: q, limit: 8 },
      })
      setResults(data.data ?? data ?? [])
      setOpen(true)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300)
    return () => clearTimeout(timer)
  }, [query, search])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (selectedCompany) {
    return (
      <div className='flex items-center gap-2 p-3 rounded-lg bg-[var(--glass-bg)] border border-[var(--brand-primary)]/30'>
        <Building2 size={16} className='text-[var(--brand-primary)] shrink-0' />
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-medium text-[var(--font-color-body)] truncate'>
            {selectedCompany.nome || selectedCompany.razaoSocial}
          </p>
          {selectedCompany.cnpj && (
            <p className='text-[11px] text-[var(--font-color-muted)] font-mono'>
              {selectedCompany.cnpj}
            </p>
          )}
        </div>
        <button
          onClick={onClear}
          className='p-1.5 rounded-md hover:bg-white/[0.06] text-[var(--text-muted)] hover:text-red-400 transition-colors'
        >
          <X size={14} />
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className='relative'>
      <div className='relative'>
        <Search size={13} className='absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]' />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!e.target.value.trim()) setResults([])
          }}
          onFocus={() => { if (results.length > 0) setOpen(true) }}
          placeholder='Buscar empresa por nome ou CNPJ...'
          className={cn(
            'w-full bg-[var(--glass-bg)] border border-[var(--glass-border)]',
            'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
            'rounded-md pl-9 pr-3 py-2 outline-none text-[13px]',
            'focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary-muted)]',
            error && 'border-[#e5484d]',
          )}
        />
        {loading && (
          <Loader2 size={13} className='absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] animate-spin' />
        )}
      </div>

      {error && (
        <p className='text-[#e5484d] text-[11px] mt-1'>{error}</p>
      )}

      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className='absolute z-20 mt-1 w-full rounded-lg bg-[var(--bg-card)] border border-[var(--glass-border)] shadow-xl overflow-hidden'
          >
            {results.map((company) => (
              <button
                key={company.id}
                onClick={() => {
                  onSelect(company)
                  setOpen(false)
                  setQuery('')
                }}
                className='w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--glass-bg)] transition-colors border-b border-[var(--glass-border)] last:border-0'
              >
                <Building2 size={14} className='text-[var(--brand-primary)] shrink-0' />
                <div className='min-w-0'>
                  <p className='text-sm font-medium text-[var(--font-color-body)] truncate'>
                    {company.nome || company.razaoSocial}
                  </p>
                  <div className='flex items-center gap-2 text-[11px] text-[var(--font-color-muted)]'>
                    {company.cnpj && <span className='font-mono'>{company.cnpj}</span>}
                    {company.cidade && <span>{company.cidade}/{company.estado}</span>}
                  </div>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
